export const CHECKOUT_URL = "https://musculoskeletal-en.impultienda.ar/checkout/";

export const images = {
  mockup: "https://cdn.magicpatterns.com/uploads/cAPMouRqRuqrMoER9RPZPY/MOCKUP_MUSCULO-EN.png",
  author: "https://cdn.magicpatterns.com/uploads/czts4qJkpvgMc9Zr1XXtT4/RYAN_CARTER_-_SPINE.png",
};

export const beforeItems = [
  "You sometimes feel unsure about which findings deserve further investigation.",
  "You spend too much time searching through notes and reference materials.",
  "You struggle to organize complex patient presentations into a coherent assessment.",
  "You worry about overlooking important warning signs.",
  "You find it difficult to explain your clinical reasoning clearly.",
  "Every unfamiliar case feels like starting from scratch.",
];

export const afterItems = [
  "You follow a structured framework for gathering relevant clinical information.",
  "You organize subjective findings and physical examination results more systematically.",
  "You have a dedicated reference for recognizing important red flags.",
  "You use regional assessment frameworks to organize your examination.",
  "You document your clinical reasoning more consistently.",
  "You have a repeatable process to support your professional judgment.",
];

export const familiarItems = [
  {
    title: "You Have a Patient With Persistent Pain, but the Findings Don’t Quite Fit.",
    text: "You have completed your initial examination, but some symptoms or clinical findings leave you uncertain about what to investigate next.",
  },
  {
    title: "You Spend Too Much Time Searching for Assessment Information.",
    text: "You have notes, textbooks, and saved resources, but the information you need is scattered across different places.",
  },
  {
    title: "You Want to Become More Consistent With Red-Flag Screening.",
    text: "You understand that certain symptoms and clinical findings require additional attention, but you want a clearer way to organize your screening process.",
  },
  {
    title: "You Struggle to Connect Subjective Findings With Your Physical Examination.",
    text: "You collect information, perform tests, and observe movement, but turning everything into a coherent clinical picture can be challenging.",
  },
  {
    title: "You Want to Explain Your Clinical Reasoning More Clearly.",
    text: "You want to communicate why you are investigating certain findings, considering alternative explanations, or recommending further assessment.",
  },
  {
    title: "You Want a Repeatable Assessment Framework.",
    text: "You don’t want to reinvent your process with every patient. You want a structured reference that helps you approach each case systematically while adapting to the individual in front of you.",
  },
];

export const volumes = [
  {
    label: "Volume I",
    accent: "#1D6FE0",
    title: "The Structured Musculoskeletal Assessment Blueprint",
    intro: "Learn how to organize an initial assessment from the patient’s presenting complaint to the findings that guide your next steps.",
    bullets: [
      "A structured approach to the subjective history.",
      "Questions about symptom onset, behavior, and progression.",
      "Relevant medical history and contextual factors.",
      "Functional limitations and patient-reported concerns.",
      "How to organize your findings before proceeding with further examination.",
      "A practical framework for documenting your initial assessment.",
    ],
    goal: "Give your initial evaluation a clearer structure instead of approaching each appointment without a consistent framework.",
    note: "",
  },
  {
    label: "Volume II",
    accent: "#DC2626",
    title: "The Musculoskeletal Red Flags Field Guide",
    intro: "Recognizing when a presentation may require further investigation is an essential part of responsible clinical practice. This volume helps you organize your understanding of potentially concerning findings.",
    bullets: [
      "The concept of red flags in musculoskeletal assessment.",
      "Important symptom patterns and historical findings.",
      "Warning signs associated with potentially serious underlying conditions.",
      "Findings that may require urgent medical evaluation.",
      "How to document relevant concerns.",
      "General principles for referral and escalation.",
    ],
    goal: "Help you develop a more systematic approach to screening for findings that should not be overlooked.",
    note: "The guide addresses relevant presentations involving the spine and other musculoskeletal regions, with emphasis on recognizing limitations and following appropriate referral pathways. This educational resource does not replace clinical judgment, local referral protocols, or emergency procedures.",
  },
  {
    label: "Volume III",
    accent: "#15803D",
    title: "The Regional Assessment Atlas: Spine, Shoulder, Hip & Knee",
    intro: "Different anatomical regions present different assessment challenges. This volume organizes key considerations for common musculoskeletal presentations into a practical regional reference.",
    bullets: [
      "Cervical spine assessment considerations.",
      "Thoracic and lumbar spine presentations.",
      "Shoulder assessment frameworks.",
      "Hip and groin assessment considerations.",
      "Knee assessment frameworks.",
      "Ankle and foot assessment considerations.",
      "Functional observations and relevant examination findings.",
      "Considerations for selecting and interpreting physical examination procedures.",
    ],
    goal: "Help you organize regional assessment information without treating individual orthopedic tests as definitive diagnostic tools.",
    note: "",
  },
  {
    label: "Volume IV",
    accent: "#7C3AED",
    title: "Clinical Reasoning: From Findings to Working Hypotheses",
    intro: "Collecting information is only one part of the assessment process. You also need to understand how the findings relate to one another. In this volume, I focus on organizing clinical reasoning.",
    bullets: [
      "How to synthesize subjective and objective findings.",
      "How to formulate and revise working hypotheses.",
      "How to consider alternative explanations.",
      "How to identify missing information.",
      "How to interpret findings in the context of the full presentation.",
      "How to document uncertainty and limitations.",
      "When additional assessment or referral should be considered.",
    ],
    goal: "Develop a more coherent approach to clinical reasoning instead of relying on a single symptom or test result.",
    note: "",
  },
  {
    label: "Volume V",
    accent: "#EA580C",
    title: "Differential Assessment & Clinical Decision Pathways",
    intro: "Musculoskeletal symptoms can have overlapping presentations. A structured approach can help you organize what you know, what remains uncertain, and what should happen next.",
    bullets: [
      "Educational decision pathways for common musculoskeletal presentations.",
      "How to organize competing clinical hypotheses.",
      "Findings that may justify additional investigation.",
      "Factors that influence the choice of further assessment.",
      "Principles for determining when routine assessment is insufficient.",
      "Referral and escalation considerations.",
      "Documentation frameworks for communicating your reasoning.",
    ],
    goal: "Make your assessment process more systematic while respecting the limits of physiotherapy assessment and your professional scope of practice.",
    note: "",
  },
  {
    label: "Volume VI",
    accent: "#1D4ED8",
    title: "Clinical Casebook: Practice Your Musculoskeletal Reasoning",
    intro: "Knowledge becomes more useful when you practice applying it to realistic situations. This volume uses educational case scenarios to help you review the assessment process.",
    bullets: [
      "Hypothetical patient presentations.",
      "Subjective history and relevant background information.",
      "Selected examination findings.",
      "Questions to guide your interpretation.",
      "Opportunities to identify missing information.",
      "Example reasoning pathways.",
      "Discussion of potential warning signs and referral considerations.",
      "Reflection questions to help you identify gaps in your reasoning.",
    ],
    goal: "Practice organizing clinical information before applying your knowledge in real-world professional settings.",
    note: "The cases are educational scenarios, not substitutes for individualized patient assessment.",
  },
];

export const bonuses = [
  {
    label: "Resource 1",
    accent: "#1D6FE0",
    title: "The Initial Assessment Checklist",
    image: "https://cdn.magicpatterns.com/uploads/9Pu6yGxe1VzPstA8QxsKiy/BONUS_1_MUSCULO-EN.png",
    intro: "A structured checklist to help you organize the main components of an initial musculoskeletal evaluation. Use it to review:",
    bullets: [
      "Presenting complaint and symptom history.",
      "Relevant medical background.",
      "Functional limitations.",
      "Important screening questions.",
      "Examination planning.",
      "Documentation and follow-up considerations.",
    ],
    outro: "Designed to help you maintain a consistent process without replacing individualized clinical judgment.",
  },
  {
    label: "Resource 2",
    accent: "#DC2626",
    title: "The Red Flag Screening Worksheet",
    image: "https://cdn.magicpatterns.com/uploads/q8aUvS2h4Vot9JBvaj9dZq/BONUS_2_MUSCULO-EN.png",
    intro: "A practical worksheet for documenting relevant symptoms, historical findings, concerning changes, and decisions about further assessment or referral.",
    bullets: [],
    outro: "Use it to organize your screening process and record the reasoning behind your next steps.",
  },
  {
    label: "Resource 3",
    accent: "#15803D",
    title: "The Regional Assessment Quick Reference",
    image: "https://cdn.magicpatterns.com/uploads/3yCVU8fvvFv7qX673CMyR7/BONUS_3_MUSCULO-EN.png",
    intro: "A compact reference organized by anatomical region. Review key assessment considerations for the cervical spine, lumbar spine, shoulder, hip, knee, ankle, and foot.",
    bullets: [],
    outro: "It helps you identify which areas of your assessment may need further attention without suggesting that a single test can confirm or exclude a diagnosis.",
  },
  {
    label: "Resource 4",
    accent: "#7C3AED",
    title: "The Clinical Reasoning & Differential Assessment Matrix",
    image: "https://cdn.magicpatterns.com/uploads/5CvJEXcprjSetmvbLXsqKr/BONUS_4_MUSCULO-EN.png",
    intro: "A structured worksheet for organizing:",
    bullets: [
      "Relevant subjective findings.",
      "Objective examination results.",
      "Potential working hypotheses.",
      "Findings that support or challenge each hypothesis.",
      "Missing information.",
      "Reasons to consider further assessment or referral.",
    ],
    outro: "Use it to make your reasoning more transparent and easier to document.",
  },
  {
    label: "Resource 5",
    accent: "#EA580C",
    title: "The Musculoskeletal Case Review Workbook",
    image: "https://cdn.magicpatterns.com/uploads/fsxXYMuN1tZJFXRasyhYZM/BONUS_5_MUSCULO-EN.png",
    intro: "A collection of educational exercises designed to help you practice the assessment process. Each exercise encourages you to:",
    bullets: [
      "Review the presenting complaint.",
      "Identify relevant information.",
      "Recognize findings that require additional attention.",
      "Consider alternative explanations.",
      "Decide what information is still missing.",
      "Explain your reasoning.",
    ],
    outro: "The workbook helps reinforce learning through practice rather than passive reading alone.",
  },
];

export const testimonials = [
  {
    name: "Sarah Mitchell",
    role: "Physiotherapist",
    location: "Australia",
    photo: "https://cdn.magicpatterns.com/patterns/generated-images/0635549a-c988-4021-9b4f-05ad84f4dd27.jpg",
    quote: "Having the red-flag guide and the screening worksheet side by side changed how I structure my first sessions. My notes are cleaner and I feel far more deliberate about what I investigate next.",
  },
  {
    name: "Daniel Ortega",
    role: "MSK Physiotherapist",
    location: "Canada",
    photo: "https://cdn.magicpatterns.com/patterns/generated-images/baca3e20-209a-4534-ba25-968d6ec67629.jpg",
    quote: "After 12 years in practice I didn’t expect much, but the reasoning matrix made my thinking visible. Explaining my referral decisions to GPs is much easier now.",
  },
  {
    name: "Amara Johnson",
    role: "Physiotherapy Student",
    location: "United Kingdom",
    photo: "https://cdn.magicpatterns.com/patterns/generated-images/eef4db5e-f641-4ecc-91d5-ba2aa1c3210a.jpg",
    quote: "The casebook is exactly what I needed before placement. Working through the cases showed me the gaps in my reasoning before I ever met a real patient.",
  },
  {
    name: "Kenji Tanaka",
    role: "Sports Physiotherapist",
    location: "United States",
    photo: "https://cdn.magicpatterns.com/patterns/generated-images/565c00db-9d20-4010-85c3-d9be8511927f.jpg",
    quote: "The regional quick reference lives on my tablet. It doesn’t replace my judgment — it keeps my examination organized when the clinic gets busy.",
  },
];

export const differentElements = [
  { title: "Structure", text: "Organize your initial evaluation using a consistent framework." },
  { title: "Screening", text: "Review important warning signs and understand when additional assessment may be necessary." },
  { title: "Regional Assessment", text: "Use anatomy-specific references to organize your examination." },
  { title: "Clinical Reasoning", text: "Bring the findings together while recognizing uncertainty and alternative explanations." },
  { title: "Practice", text: "Use educational case scenarios to review how the different elements work together." },
];

export const faqs = [
  {
    q: "How Will I Receive the Material?",
    a: "After your payment is confirmed, you’ll receive instructions for accessing your digital purchase. The six volumes and five bonuses will be provided in the formats specified at checkout.",
  },
  {
    q: "Is This Suitable for Students?",
    a: "The collection may be useful for physiotherapy students who already have a foundation in musculoskeletal anatomy and assessment concepts. It is intended as a supplementary educational resource, not as a replacement for supervised clinical education.",
  },
  {
    q: "Is This Suitable for Practicing Physiotherapists?",
    a: "The material is designed for physiotherapists who want to organize their assessment process, review red flags, and reinforce their clinical reasoning. Its usefulness will depend on your existing training, professional scope, and clinical setting.",
  },
  {
    q: "Does This Collection Teach Me How to Diagnose Every Musculoskeletal Condition?",
    a: "No. The collection provides educational assessment frameworks and clinical reasoning resources. It does not guarantee diagnostic accuracy or replace a comprehensive patient evaluation.",
  },
  {
    q: "Does It Include Red Flags?",
    a: "Yes. One of the six volumes focuses on red flags, and one of the five bonuses is a dedicated screening worksheet. These resources support learning and documentation. They do not replace local emergency procedures, referral guidelines, or appropriate medical evaluation.",
  },
  {
    q: "Can I Use the Material With Patients?",
    a: "You may consult educational references to support your professional learning and preparation. However, all clinical decisions must be individualized and consistent with your qualifications, scope of practice, applicable guidelines, and the patient’s presentation. The collection is not a stand-alone clinical protocol.",
  },
  {
    q: "Do I Need Any Special Software?",
    a: "You will need a compatible device and software capable of opening the supplied digital file formats. Any specific format requirements will be stated at checkout.",
  },
  {
    q: "Is This a Subscription?",
    a: "No. The listed price is a one-time payment of $27, with no recurring subscription.",
  },
  {
    q: "Will I Have Lifetime Access?",
    a: "The offer includes lifetime access to the purchased files, subject to the access terms disclosed at checkout.",
  },
  {
    q: "What If I’m Not Satisfied?",
    a: "Your purchase is covered by a 7-day money-back guarantee. Contact the support address provided at checkout within the guarantee period to request a refund under the published policy.",
  },
];

export const recentBuyers = [
  { name: "María Fernández", country: "Mexico", ago: "2 minutes ago" },
  { name: "Javier Morales", country: "Colombia", ago: "4 minutes ago" },
  { name: "Lucía Romero", country: "Argentina", ago: "just now" },
  { name: "Carlos Méndez", country: "Chile", ago: "6 minutes ago" },
  { name: "Valentina Ríos", country: "Peru", ago: "1 minute ago" },
  { name: "Andrés Castillo", country: "Spain", ago: "3 minutes ago" },
  { name: "Camila Torres", country: "Ecuador", ago: "8 minutes ago" },
  { name: "Diego Herrera", country: "Uruguay", ago: "5 minutes ago" },
  { name: "Sofía Ramírez", country: "Costa Rica", ago: "just now" },
  { name: "Mateo Vargas", country: "Bolivia", ago: "7 minutes ago" },
  { name: "Daniela Ruiz", country: "Guatemala", ago: "2 minutes ago" },
  { name: "Alejandro Pérez", country: "Dominican Republic", ago: "9 minutes ago" },
  { name: "Gabriela Sánchez", country: "Paraguay", ago: "4 minutes ago" },
  { name: "Fernando Díaz", country: "Venezuela", ago: "1 minute ago" },
  { name: "Isabella Cruz", country: "Panama", ago: "3 minutes ago" },
];
